import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useEmail } from '../../contexts/EmailContext';
import EmailTemplateEditor from './EmailTemplateEditor';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import { FiCalendar, FiCopy, FiEdit, FiEye, FiFilter, FiMail, FiPlus, FiSearch, FiTrash2, FiTrendingUp, FiUsers } from 'react-icons/fi';


const EmailTemplateLibrary = () => {
  const { getEmailTemplates, deleteTemplate } = useEmail();
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  const templates = getEmailTemplates();

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || template.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = [
    { value: 'all', label: 'All Templates' },
    { value: 'initial_outreach', label: 'Initial Outreach' },
    { value: 'follow_up', label: 'Follow-up' },
    { value: 'nurturing', label: 'Nurturing' },
    { value: 'meeting_invitation', label: 'Meeting Invitations' },
    { value: 'reengagement', label: 'Re-engagement' },
    { value: 'onboarding', label: 'Onboarding' }
  ];

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setShowTemplateEditor(true);
  };

  const handleCloseEditor = () => {
    setShowTemplateEditor(false);
    setSelectedTemplate(null);
  };

  const getCategoryIcon = (category) => {
    const icons = {
      initial_outreach: FiMail,
      follow_up: FiTrendingUp,
      nurturing: FiUsers,
      meeting_invitation: FiCalendar,
      reengagement: FiTrendingUp,
      onboarding: FiUsers
    };
    return icons[category] || FiMail;
  };

  const getCategoryColor = (category) => {
    const colors = {
      initial_outreach: 'bg-blue-100 text-blue-800',
      follow_up: 'bg-green-100 text-green-800',
      nurturing: 'bg-purple-100 text-purple-800',
      meeting_invitation: 'bg-orange-100 text-orange-800',
      reengagement: 'bg-red-100 text-red-800',
      onboarding: 'bg-indigo-100 text-indigo-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Email Templates</h2>
          <p className="text-gray-600">
            Manage your recruitment email templates for different scenarios
          </p>
        </div>
        <button
          onClick={() => setShowTemplateEditor(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="text-sm" />
          Create Template
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="relative flex-1">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiFilter} className="text-gray-400" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {categories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map((template, index) => {
          const CategoryIcon = getCategoryIcon(template.category);
          return (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              {/* Template Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={CategoryIcon} className="text-white text-lg" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{template.name}</h3>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(template.category)}`}>
                      {template.category.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Template Details */}
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                <div className="space-y-2">
                  <div className="text-xs text-gray-500">
                    <strong>Subject:</strong> {template.subject}
                  </div>
                  <div className="text-xs text-gray-500">
                    <strong>Usage:</strong> {template.usage || 0} times
                  </div>
                </div>
              </div>

              {/* Template Preview */}
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-xs text-gray-600 line-clamp-3">
                  {template.content.substring(0, 150)}...
                </p>
              </div>

              {/* Template Actions */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEditTemplate(template)}
                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Edit template"
                  >
                    <SafeIcon icon={FiEdit} className="text-sm" />
                  </button>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(template.content);
                      toast.success('Template copied to clipboard');
                    }}
                    className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                    title="Copy template"
                  >
                    <SafeIcon icon={FiCopy} className="text-sm" />
                  </button>
                  {!template.isDefault && (
                    <button
                      onClick={() => deleteTemplate(template.id)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete template"
                    >
                      <SafeIcon icon={FiTrash2} className="text-sm" />
                    </button>
                  )}
                </div>
                <button className="flex items-center gap-1 px-3 py-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
                  <SafeIcon icon={FiEye} className="text-xs" />
                  Preview
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <SafeIcon icon={FiMail} className="text-4xl text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || filterCategory !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'Create your first email template to get started.'
            }
          </p>
          <button
            onClick={() => setShowTemplateEditor(true)}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Create Template
          </button>
        </div>
      )}

      {/* Template Editor Modal */}
      {showTemplateEditor && (
        <EmailTemplateEditor
          template={selectedTemplate}
          onClose={handleCloseEditor}
        />
      )}
    </div>
  );
};

export default EmailTemplateLibrary;